function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

class PredictiveSearch extends HTMLElement {
    constructor() {
      super();
      this.cachedResults = {};
      this.input = null;
      this.predictiveSearchResults = null;
      this.maxResults = 6;
      this.onFormSubmit = this.onFormSubmit.bind(this);
      this.onFocus = this.onFocus.bind(this);
      this.onFocusOut = this.onFocusOut.bind(this);
      this.onKeyup = this.onKeyup.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
    }

    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
      this.setupEventListeners();
    }

    setupEventListeners() {
      const form = this.querySelector('form.search');
      if (form) form.addEventListener('submit', this.onFormSubmit);

      if (this.input) {
        this.input.addEventListener('input', debounce(() => this.onChange(), 300));
        this.input.addEventListener('focus', this.onFocus);
      }

      this.addEventListener('focusout', this.onFocusOut);
      this.addEventListener('keyup', this.onKeyup);
      this.addEventListener('keydown', this.onKeydown);
    }
  
    getQuery() {
      return this.input ? this.input.value.trim() : '';
    }
  
    onChange() {
      const searchTerm = this.getQuery();
  
      if (!searchTerm.length) {
        this.close(true);
        return;
      }
  
      this.getSearchResults(searchTerm);
    }
  
    onFormSubmit(event) {
      if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
    }
  
    onFocus() {
      const searchTerm = this.getQuery();
  
      if (!searchTerm.length) return;
  
      if (this.getAttribute('results') === 'true') {
        this.open();
      } else {
        this.getSearchResults(searchTerm);
      }
    } 
  
    onFocusOut() {
      setTimeout(() => {
        if (!this.contains(document.activeElement)) this.close();
      })
    }
  
    onKeyup(event) {
      if (!this.getQuery().length) this.close(true);
      event.preventDefault();
  
      switch (event.code) {
        case 'ArrowUp':
          this.switchOption('up')
          break;
        case 'ArrowDown':
          this.switchOption('down');
          break;
        case 'Enter':
          this.selectOption();
          break;
      }
    }
  
    onKeydown(event) {
      // Prevent the cursor from moving in the input when using the up and down arrow keys
      if (
        event.code === 'ArrowUp' ||
        event.code === 'ArrowDown'
      ) {
        event.preventDefault();
      }
    }
  
    switchOption(direction) {
      if (!this.getAttribute('open')) return;
      
      const moveUp = direction === 'up';
      const selectedElement = this.querySelector('[aria-selected="true"]');
      const allElements = this.querySelectorAll('li');
      let activeElement = this.querySelector('li');
  
      if (moveUp && !selectedElement) return;

      if (this.statusElement) this.statusElement.textContent = ''; 
  
      if (!moveUp && selectedElement) {
        activeElement = selectedElement.nextElementSibling || allElements[0];
      } else if (moveUp) {
        activeElement = selectedElement.previousElementSibling || allElements[allElements.length - 1];
      }
  
      if (activeElement === selectedElement) return;
  
      activeElement.setAttribute('aria-selected', true);
      if (selectedElement) selectedElement.setAttribute('aria-selected', false);

      this.setLiveRegionText(activeElement.textContent);
      if (this.input) this.input.setAttribute('aria-activedescendant', activeElement.id);
    }
  
    selectOption() {
      const selectedProduct = this.querySelector('[aria-selected="true"] a, [aria-selected="true"] button');
  
      if (selectedProduct) selectedProduct.click();
    }
  
    getSearchResults(searchTerm) {
      const queryKey = searchTerm.replace(" ", "-").toLowerCase();
      this.setLiveRegionLoadingState();
  
      if (this.cachedResults[queryKey]) {
        this.renderSearchResults(this.cachedResults[queryKey]);
        return;
      }
  
      const baseUrl = (typeof routes !== 'undefined' && routes.predictive_search_url)
        ? routes.predictive_search_url
        : (window.Shopify?.routes?.root || '/') + 'search/suggest';
      const url = `${baseUrl}?q=${encodeURIComponent(searchTerm)}&resources[type]=product,collection&resources[options][fields]=title,product_type,variants.title,body&resources[limit]=${this.maxResults}&resources[limit_scope]=each&section_id=predictive-search`;

      fetch(url)
        .then((response) => {
          if (!response.ok) {
            var error = new Error(response.status);
            this.close();
            throw error;
          }
          return response.text();
        })
        .then((text) => {
          const doc = new DOMParser().parseFromString(text, 'text/html');
          let sectionEl = doc.querySelector('#shopify-section-predictive-search');
          if (!sectionEl) sectionEl = doc.querySelector('[id^="shopify-section-"]');
          const resultsMarkup = sectionEl ? sectionEl.innerHTML : '';
          if (!resultsMarkup) return;
          this.cachedResults[queryKey] = resultsMarkup;
          this.renderSearchResults(resultsMarkup);
        })
        .catch((error) => {
          this.close();
          throw error;
        });
    }
  
    setLiveRegionLoadingState() {
      this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
      this.loadingText = this.loadingText || this.getAttribute('data-loading-text');
  
      this.setLiveRegionText(this.loadingText);
      this.setAttribute('loading', true);
    }
  
    setLiveRegionText(statusText) {
      if (!this.statusElement) return;
      this.statusElement.setAttribute('aria-hidden', 'false');
      this.statusElement.textContent = statusText;
      setTimeout(() => {
        this.statusElement.setAttribute('aria-hidden', 'true');
      }, 1000);
    }
  
    renderSearchResults(resultsMarkup) {
      if (!this.predictiveSearchResults) return;
      this.predictiveSearchResults.innerHTML = resultsMarkup;
      this.setAttribute('results', true);  
  
      this.setLiveRegionResults();
      this.open();
    }
  
    setLiveRegionResults() {
      this.removeAttribute('loading');
      const countEl = this.querySelector('[data-predictive-search-live-region-count-value]');
      if (countEl) this.setLiveRegionText(countEl.textContent);
    } 
  
    getResultsMaxHeight() {
      console.log(window.innerHeight);
      this.resultsMaxHeight = window.innerHeight - document.getElementById('shopify-section-header').getBoundingClientRect().bottom;
      return this.resultsMaxHeight;
    }
  
    open() {
      if (!this.predictiveSearchResults) return;
      this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
      this.setAttribute('open', true);
      if (this.input) this.input.setAttribute('aria-expanded', true);
      document.body.classList.add('overflow-hidden');
    }
  
    close(clearSearchTerm = false) {
      if (clearSearchTerm && this.input) {
        this.input.value = '';
        this.removeAttribute('results');
      }

      const selected = this.querySelector('[aria-selected="true"]');
      if (selected) selected.setAttribute('aria-selected', false);

      if (this.input) {
        this.input.setAttribute('aria-activedescendant', '');
        this.input.setAttribute('aria-expanded', false);
      }
      this.removeAttribute('open');
      this.resultsMaxHeight = false;
      if (this.predictiveSearchResults) this.predictiveSearchResults.removeAttribute('style');
      document.body.classList.remove('overflow-hidden');
    }
  }
  
  customElements.define('predictive-search', PredictiveSearch);
  